import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay/classic';
import DocumentTitle from 'react-document-title';

import PipelineScheduleUpdateMutation from 'app/mutations/PipelineScheduleUpdate';
import PipelineScheduleDeleteMutation from 'app/mutations/PipelineScheduleDelete';

import Button from 'app/components/shared/Button';
import PageHeader from 'app/components/shared/PageHeader';
import Panel from 'app/components/shared/Panel';
import Emojify from 'app/components/shared/Emojify';
import FriendlyTime from 'app/components/shared/FriendlyTime';

import permissions from 'app/lib/permissions';

import Build from './build';

class Show extends React.Component {
  static propTypes = {
    pipelineSchedule: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      cronline: PropTypes.string.isRequired,
      label: PropTypes.string,
      commit: PropTypes.string,
      branch: PropTypes.string,
      message: PropTypes.string,
      env: PropTypes.arrayOf(PropTypes.string),
      enabled: PropTypes.bool.isRequired,
      failedMessage: PropTypes.string,
      failedAt: PropTypes.string,
      nextBuildAt: PropTypes.string,
      pipeline: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        organization: PropTypes.shape({
          slug: PropTypes.string.isRequired
        }).isRequired,
        permissions: PropTypes.shape({
          buildCreate: PropTypes.shape({
            allowed: PropTypes.bool.isRequired
          }).isRequired
        }).isRequired
      }).isRequired,
      builds: PropTypes.shape({
        edges: PropTypes.arrayOf(
          PropTypes.shape({
            node: PropTypes.shape({
              id: PropTypes.string.isRequired
            }).isRequired
          }).isRequired
        )
      }).isRequired,
      createdBy: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired,
      ownedBy: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }),
      permissions: PropTypes.shape({
        pipelineScheduleUpdate: PropTypes.shape({
          allowed: PropTypes.bool.isRequired
        }).isRequired,
        pipelineScheduleDelete: PropTypes.shape({
          allowed: PropTypes.bool.isRequired
        }).isRequired
      }).isRequired
    }),
    viewer: PropTypes.shape({
      user: PropTypes.shape({
        id: PropTypes.string.isRequired
      }).isRequired
    }).isRequired
  };

  static contextTypes = {
    router: PropTypes.object.isRequired
  };

  state = {
    deleting: false,
    reEnabling: false,
    removingOwnership: false
  }

  render() {
    const { pipelineSchedule } = this.props;
    // If the schedule doesn't exist, that means that it's just been deleted.
    // And since we require all the schedule to render this component, we'll
    // just short-circut the re-render when it's gone. This isn't great, maybe
    // there's a beter way?
    if (!pipelineSchedule) {
      return null;
    }

    return (
      <DocumentTitle title={pipelineSchedule.label || pipelineSchedule.cronline}>
        <div>
          <PageHeader>
            <PageHeader.Title>
              <Emojify
                text={pipelineSchedule.label || "No description"}
              />
              {pipelineSchedule.enabled ? null : (
                <span
                  style={{
                    fontSize: 12,
                    verticalAlign: 'middle'
                  }}
                  className="mx1 regular border border-red rounded red px1"
                >
                  Disabled
                </span>
              )}
            </PageHeader.Title>
            <PageHeader.Description>
              {pipelineSchedule.cronline}
            </PageHeader.Description>
            <PageHeader.Menu>
              {this.renderMenu()}
            </PageHeader.Menu>
          </PageHeader>

          {this.renderRemoveOwnershipPanel()}

          {this.renderFailureMessage()}

          <Panel className="mb4">
            <Panel.Section>
              <div><strong>Message</strong></div>
              <div className="mb2 dark-gray">{pipelineSchedule.message || "Scheduled build"}</div>

              <div><strong>Commit</strong></div>
              <div className="mb2 dark-gray"><code className="code">{pipelineSchedule.commit}</code></div>

              <div><strong>Branch</strong></div>
              <div className="mb2 dark-gray">{pipelineSchedule.branch}</div>

              {this.renderEnv()}

              {pipelineSchedule.ownedBy && (
                <React.Fragment>
                  <div><strong>Builds Created By</strong></div>
                  <div className="mb2 dark-gray">{pipelineSchedule.ownedBy.name}</div>
                </React.Fragment>
              )}

              <div><strong>Schedule Created By</strong></div>
              <div className="mb2 dark-gray">{pipelineSchedule.createdBy.name}</div>
            </Panel.Section>
          </Panel>

          <Panel>
            <Panel.Header>Recent Builds</Panel.Header>
            <Panel.Row>
              {this.renderRecentBuildsHeading()}
            </Panel.Row>
            {pipelineSchedule.builds.edges.map((edge) => (
              <Build
                key={edge.node.id}
                build={edge.node}
              />
            ))}
          </Panel>
        </div>
      </DocumentTitle>
    );
  }

  renderRemoveOwnershipPanel() {
    const { pipelineSchedule } = this.props;

    if (!pipelineSchedule.ownedBy) {
      return null;
    }

    return (
      <Panel className="mb4">
        <Panel.Header>
          ⚠️ Requires Build Ownership Migration
        </Panel.Header>
        <Panel.Section>
          <p>This schedule uses the deprecated build ownership property, which will be removed January 30, 2020. Please migrate this schedule by removing build ownership before January 30, using the <strong className="bold">Remove Build Ownership</strong> button below.</p>
          {/* <p>This schedule uses the <a href="" className="lime hover-lime text-decoration-none hover-underline">deprecated build ownership property</a>, which will be removed January 30, 2020. Please migrate this schedule by removing build ownership before January 30, using the <strong className="bold">Remove Build Ownership</strong> button below.</p> */}
          <p>Removing build ownership will remove the build association with the user <strong className="bold">{pipelineSchedule.ownedBy.name}</strong>, and builds will no longer have an associated user (e.g. the <code className="code">BUILDKITE_BUILD_CREATOR</code> environment variable will be empty).</p>
          <p>You can read more about the deprecation on <a href="https://buildkite.com/changelog/79-schedules-no-longer-need-a-user" className="lime hover-lime text-decoration-none hover-underline">the changelog</a>, or contact <a href="mailto:support@buildkite.com" className="lime hover-lime text-decoration-none hover-underline">support@buildkite.com</a> if you have any questions.</p>
        </Panel.Section>
        <Panel.Section>
          <Button
            loading={this.state.removingOwnership ? "Removing Build Ownership…" : false}
            onClick={this.handleScheduleRemoveOwnershipClick}
          >
            Remove Build Ownership
          </Button>
        </Panel.Section>
      </Panel>
    );
  }

  renderFailureMessage() {
    const { pipelineSchedule } = this.props;

    if (!pipelineSchedule.failedAt) {
      return null;
    }

    return (
      <div className="mb4 p2 border border-red rounded red flex items-center">
        <span className="m1 pr2">
          <p className="mt0 bold">This schedule was automatically disabled <FriendlyTime capitalized={false} value={pipelineSchedule.failedAt} /></p>
          <p className="mb0">{pipelineSchedule.failedMessage}</p>
        </span>
        <Button
          className="m1"
          theme="danger"
          loading={this.state.reEnabling ? "Re-Enabling…" : false}
          onClick={this.handleScheduleReEnableClick}
        >
          Re-Enable
        </Button>
      </div>
    );
  }

  renderRecentBuildsHeading() {
    const { pipelineSchedule } = this.props;

    if (!pipelineSchedule.enabled) {
      return (
        <span className="dark-gray py1">
          This pipeline schedule is currently disabled.
        </span>
      );
    }

    return (
      <React.Fragment>
        Next build scheduled for <FriendlyTime capitalized={false} value={pipelineSchedule.nextBuildAt} />
      </React.Fragment>
    );
  }

  renderEnv() {
    if (this.props.pipelineSchedule.env.length !== 0) {
      return (
        <div>
          <div><strong>Environment Variables</strong></div>
          <div className="mb2 dark-gray"><pre><code className="code">{this.props.pipelineSchedule.env.join("\n")}</code></pre></div>
        </div>
      );
    }

    return null;
  }

  renderMenu() {
    return permissions(this.props.pipelineSchedule.permissions).collect(
      {
        allowed: "pipelineScheduleUpdate",
        render: (idx) => {
          const pipeline = this.props.pipelineSchedule.pipeline;
          const organization = this.props.pipelineSchedule.pipeline.organization;

          return (
            <PageHeader.Button
              key={idx}
              link={`/${organization.slug}/${pipeline.slug}/settings/schedules/${this.props.pipelineSchedule.uuid}/edit`}
            >
              Edit
            </PageHeader.Button>
          );
        }
      },
      {
        allowed: "pipelineScheduleDelete",
        render: (idx) => (
          <PageHeader.Button
            key={idx}
            loading={this.state.deleting ? "Deleting…" : false}
            onClick={this.handleScheduleDeleteClick}
          >
            Delete
          </PageHeader.Button>
        )
      }
    );
  }

  handleScheduleReEnableClick = () => {
    this.setState({ reEnabling: true });

    const mutation = new PipelineScheduleUpdateMutation({
      enabled: true,
      pipelineSchedule: this.props.pipelineSchedule
    });

    Relay.Store.commitUpdate(mutation, {
      onSuccess: this.handleUpdateMutationSuccess,
      onFailure: this.handleUpdateMutationFailure
    });
  }

  handleScheduleRemoveOwnershipClick = () => {
    this.setState({ removingOwnership: true });

    const mutation = new PipelineScheduleUpdateMutation({
      ownedById: null,
      pipelineSchedule: this.props.pipelineSchedule
    });

    Relay.Store.commitUpdate(mutation, {
      onSuccess: this.handleUpdateMutationSuccess,
      onFailure: this.handleUpdateMutationFailure
    });
  }

  handleUpdateMutationSuccess = () => {
    this.setState({ reEnabling: false, removingOwnership: false });
  }

  handleUpdateMutationFailure = (transaction) => {
    this.setState({ reEnabling: false, removingOwnership: false });

    alert(transaction.getError());
  }

  handleScheduleDeleteClick = () => {
    if (confirm("Delete this schedule?")) {
      this.setState({ deleting: true });

      const mutation = new PipelineScheduleDeleteMutation({
        pipelineSchedule: this.props.pipelineSchedule
      });

      Relay.Store.commitUpdate(mutation, {
        onSuccess: this.handleDeleteMutationSuccess,
        onFailure: this.handleDeleteMutationFailure
      });
    }
  }

  handleDeleteMutationSuccess = () => {
    const pipeline = this.props.pipelineSchedule.pipeline;
    const organization = this.props.pipelineSchedule.pipeline.organization;

    this.context.router.push(`/${organization.slug}/${pipeline.slug}/settings/schedules`);
  }

  handleDeleteMutationFailure = (transaction) => {
    this.setState({ deleting: false });

    alert(transaction.getError());
  }
}

export default Relay.createContainer(Show, {
  fragments: {
    pipelineSchedule: () => Relay.QL`
      fragment on PipelineSchedule {
        ${PipelineScheduleUpdateMutation.getFragment('pipelineSchedule')}
        ${PipelineScheduleDeleteMutation.getFragment('pipelineSchedule')}
        uuid
        cronline
        label
        nextBuildAt
        commit
        branch
        message
        env
        enabled
        failedMessage
        failedAt
        createdBy {
          id
          name
        }
        ownedBy {
          id
          name
        }
        pipeline {
          slug
          organization {
            slug
          }
          permissions {
            buildCreate {
              allowed
            }
          }
        }
        permissions {
          pipelineScheduleUpdate {
            allowed
          }
          pipelineScheduleDelete {
            allowed
          }
        }
        builds(first: 5) {
          edges {
            node {
              id
              ${Build.getFragment('build')}
            }
          }
        }
      }
    `,
    viewer: () => Relay.QL`
      fragment on Viewer {
        user {
          id
        }
      }
    `
  }
});
